import type { BeneficiaryModifiedDiscrepancy } from '@approvalmax/types';
import { Button } from '@approvalmax/ui';
import { LineItemsTable } from 'pages/requestList/components/lineItemsSection/LineItemsTable';
import { ColumnDefinition } from 'pages/requestList/selectors/types/ColumnDefinition';
import { memo, useCallback, useMemo } from 'react';

import { messages } from './ChangedBeneficiariesPopup.messages';
import {
    ButtonContainer,
    Content,
    DefaultContent,
    Description,
    DiscrepancyContainer,
    DiscrepancyName,
    StyledAlertListIcon,
    TableContainer,
} from './ChangedBeneficiariesPopup.styles';
import { ChangedBeneficiariesPopupProps } from './ChangedBeneficiariesPopup.types';

const ChangedBeneficiariesPopup = memo((props: ChangedBeneficiariesPopupProps) => {
    const { title, beneficiaryDiscrepancies, onClose, onApply } = props;

    const handleApply = useCallback(() => {
        onApply(
            beneficiaryDiscrepancies.map((descrepancy) => ({
                id: descrepancy.airwallexBeneficiaryId,
                version: descrepancy.beneficiaryVersion,
            }))
        );
    }, [beneficiaryDiscrepancies, onApply]);

    const columnDefinitions: ColumnDefinition<BeneficiaryModifiedDiscrepancy>[] = useMemo(
        () => [
            {
                id: 'beneficiary',
                name: messages.beneficiary,
                value: (li) => li.beneficiaryName,
                cell: (value: string) => <span>{value}</span>,
            },
            {
                id: 'discrepancy',
                name: messages.discrepancy,
                value: (li) => li.beneficiaryName,
                cell: (_value, li) => (
                    <DiscrepancyContainer>
                        <StyledAlertListIcon />

                        <div>
                            {li.modifiedProperties.map((property) => (
                                <DiscrepancyName key={property.value}>{property.name}</DiscrepancyName>
                            ))}
                        </div>
                    </DiscrepancyContainer>
                ),
            },
        ],
        []
    );

    return (
        <DefaultContent title={title}>
            <Content>
                <Description>{messages.description1}</Description>

                <TableContainer>
                    <LineItemsTable columnDefinitions={columnDefinitions} lineItems={beneficiaryDiscrepancies} />
                </TableContainer>

                <Description>{messages.description2}</Description>

                <ButtonContainer>
                    <Button colorTheme='red' execute={onClose}>
                        {messages.cancel}
                    </Button>

                    <Button execute={handleApply}>{messages.apply}</Button>
                </ButtonContainer>
            </Content>
        </DefaultContent>
    );
});

export default ChangedBeneficiariesPopup;
